/**
 * Text-to-Excalidraw Stream Fetch utility
 * Handles SSE streaming for direct ExcalidrawElementSkeleton generation
 */

import { parseMermaidToExcalidraw } from "@excalidraw/mermaid-to-excalidraw";

export interface TextToExcalidrawMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ImageData {
  data: string; // base64 encoded
  mimeType: string;
}

export interface AttachedFileData {
  contentBase64: string;
  mimeType: string;
  filename?: string;
}

export interface TextToExcalidrawOptions {
  url: string;
  messages: readonly TextToExcalidrawMessage[];
  chartType?: string;
  image?: ImageData;
  /** Uploaded document files; backend extracts text and appends to user_spec. */
  files?: AttachedFileData[];
  /** Logged-in user ID (10/24h). When set, backend uses logged-in quota. */
  userId?: string | null;
  /** Anonymous device ID when not logged in (2/24h). Send one of userId or anonymousId. */
  anonymousId?: string | null;
  onChunk?: (chunk: string) => void;
  onStreamCreated?: () => void;
  onPhaseUpdate?: (phase: string, status: string) => void;
  onPlanReceived?: (plan: any) => void;
  onRouteReceived?: (route: any) => void;
  onPartialElements?: (
    elements: ExcalidrawElementSkeleton[],
    totalCount: number,
  ) => void;
  signal?: AbortSignal;
  useNewPipeline?: boolean; // Use new LangChain multi-agent pipeline
  forceExecutor?: "mermaid" | "excalidraw"; // Force specific executor (debug)
  /** Pre-generated plan; when set, backend uses it as user_spec. */
  plan?: string;
  /** From Go to Draw: backend runs Summary(messages, topic) then Generation. When set, do not send plan. */
  conversationContext?: {
    messages: Array<{ role: string; content: string }>;
    topic: string;
  };
  /** Called when backend sends the summary (from Go to Draw flow); use for display and Regenerate. */
  onSummaryReceived?: (summary: string) => void;
}

export type TextToExcalidrawStreamChunk =
  | {
      type: "init";
      correlationId: string;
    }
  | {
      type: "keepalive";
      elapsed_ms?: number;
    }
  | {
      type: "phase";
      phase: string;
      status: "started" | "completed";
    }
  | {
      type: "plan";
      search_plan: any;
    }
  | {
      type: "route";
      executor_plan: any;
    }
  | {
      type: "thought";
      content: string;
      phase?: string;
    }
  | {
      type: "partial";
      elements: ExcalidrawElementSkeleton[];
      total_count: number;
    }
  | {
      type: "content";
      delta: string;
    }
  | {
      type: "optimized";
      elements: ExcalidrawElementSkeleton[];
      code: string;
      files?: Record<string, BinaryFileData>;
      executor?: string;
      diagram_type?: string;
      metadata?: any;
    }
  | {
      type: "mermaid_result";
      code: string;
      executor: string;
      diagram_type: string;
    }
  | {
      type: "processed";
      code: string;
      parseError?: string;
      executor?: string;
      diagram_type?: string;
    }
  | {
      type: "done";
      finishReason: "stop" | "length" | "content_filter" | null;
    }
  | {
      type: "error";
      error: {
        message: string;
        code?: string;
      };
    }
  | {
      type: "safety_refusal";
      message: string;
      code?: string;
    }
  | {
      type: "quota_exceeded";
      message: string;
      code?: string;
      used?: number;
      limit?: number;
      windowHours?: number;
    }
  | {
      type: "summary";
      summary: string;
    };

export interface ExcalidrawElementSkeleton {
  id?: string;
  type: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  strokeColor?: string;
  backgroundColor?: string;
  fillStyle?: string;
  strokeWidth?: number;
  strokeStyle?: string;
  roughness?: number;
  opacity?: number;
  angle?: number;
  roundness?: unknown;
  locked?: boolean;
  link?: string;
  label?: {
    text: string;
    fontSize?: number;
    fontFamily?: number;
    strokeColor?: string;
    textAlign?: "left" | "center" | "right";
    verticalAlign?: "top" | "middle" | "bottom";
  };
  // For text elements
  text?: string;
  fontSize?: number;
  fontFamily?: number;
  textAlign?: string;
  verticalAlign?: string;
  // For arrow elements
  start?: {
    type?: string;
    id?: string;
    text?: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
  end?: {
    type?: string;
    id?: string;
    text?: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
  startArrowhead?: string;
  endArrowhead?: string;
  elbowed?: boolean;
  // For frames
  children?: string[];
  name?: string;
  // For grouping
  groupIds?: string[];
  // For images
  fileId?: string;
  scale?: [number, number];
  crop?: unknown;
  // Line-specific
  polygon?: boolean;
  points?: number[][];
}

export interface BinaryFileData {
  id: string;
  dataURL: string;
  mimeType: string;
  created?: number;
  lastRetrieved?: number;
}

export interface TextToExcalidrawResult {
  elements?: ExcalidrawElementSkeleton[];
  files?: Record<string, BinaryFileData>;
  rawCode?: string;
  error?: Error;
  correlationId?: string; // Run ID from backend for telemetry
  /** Set when backend refused the prompt due to safety policy (no generation). */
  safetyRefusal?: { message: string; code?: string };
  /** Set when account has reached 24h generation quota (no generation, clear message). */
  quotaExceeded?: {
    message: string;
    code?: string;
    used?: number;
    limit?: number;
    windowHours?: number;
  };
}

const MAX_NETWORK_RETRY = 1;

const RETRYABLE_HTTP_STATUS = new Set([408, 502, 503, 504]);

const isRetryableNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  if (error.name === "AbortError") {
    return false;
  }
  const status = (error as Error & { httpStatus?: number }).httpStatus;
  if (status && RETRYABLE_HTTP_STATUS.has(status)) {
    return true;
  }
  const message = (error.message || "").toLowerCase();
  return (
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("fetch failed") ||
    message.includes("load failed") ||
    message.includes("connection") ||
    message.includes("streaming error")
  );
};

const waitForOnlineIfNeeded = async (signal?: AbortSignal): Promise<void> => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return;
  }
  if (navigator.onLine) {
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const onOnline = () => {
      window.removeEventListener("online", onOnline);
      signal?.removeEventListener("abort", onAbort);
      resolve();
    };
    const onAbort = () => {
      window.removeEventListener("online", onOnline);
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("Request aborted", "AbortError"));
    };
    window.addEventListener("online", onOnline);
    signal?.addEventListener("abort", onAbort);
  });
};

async function* parseSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
): AsyncGenerator<string, void, unknown> {
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) {
          continue;
        }

        if (trimmedLine.startsWith("data: ")) {
          const data = trimmedLine.slice(6);

          // Debug: Log raw SSE data for partial events
          try {
            const parsed = JSON.parse(data);
            if (parsed.type === "partial") {
              console.log("[SSE Debug] Raw partial data:", {
                type: parsed.type,
                elementCount: parsed.elements?.length,
                elementIds: parsed.elements?.map((el: any) => el.id),
                totalCount: parsed.count,
              });
            }
          } catch (e) {
            // Ignore parse errors for non-JSON data
          }

          yield data;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function TextToExcalidrawStreamFetch(
  options: TextToExcalidrawOptions,
): Promise<TextToExcalidrawResult> {
  const internalRetryAttempt = (options as TextToExcalidrawOptions & {
    __retryAttempt?: number;
  }).__retryAttempt ?? 0;
  const fetchId = Math.random().toString(36).substring(7);
  console.log(`[TextToExcalidraw] 🟢 Fetch START (ID: ${fetchId})`);

  const {
    url,
    messages,
    chartType = "auto",
    image,
    files,
    userId,
    anonymousId,
    plan,
    conversationContext,
    onChunk,
    onStreamCreated,
    onPhaseUpdate,
    onPlanReceived,
    onRouteReceived,
    onPartialElements,
    onSummaryReceived,
    signal,
    useNewPipeline = false,
    forceExecutor,
  } = options;

  try {
    let fullResponse = "";
    let optimizedElements: ExcalidrawElementSkeleton[] | undefined;
    let optimizedFiles: Record<string, BinaryFileData> | undefined;
    let mermaidCode: string | undefined;
    let error: Error | null = null;
    let correlationId: string | undefined; // Track run ID for telemetry
    const partialElements: ExcalidrawElementSkeleton[] = [];

    const requestBody: Record<string, unknown> = {
      messages,
      chartType,
    };

    if (image) {
      requestBody.image = image;
    }

    if (files?.length) {
      requestBody.files = files;
    }

    if (conversationContext != null) {
      requestBody.conversationContext = conversationContext;
    } else if (plan != null && plan.trim()) {
      requestBody.plan = plan.trim();
    }

    if (userId) {
      requestBody.userId = userId;
    } else if (anonymousId) {
      requestBody.anonymousId = anonymousId;
    }

    if (useNewPipeline && forceExecutor) {
      requestBody.forceExecutor = forceExecutor;
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!response.ok) {
      const text = await response.text();
      const httpError = new Error(
        text || `Request failed with status ${response.status}`,
      ) as Error & { httpStatus?: number };
      httpError.httpStatus = response.status;
      throw httpError;
    }

    const reader = response.body?.getReader();

    if (!reader) {
      throw new Error("Couldn't get reader from response body");
    }

    onStreamCreated?.();

    try {
      for await (const data of parseSSEStream(reader)) {
        if (data === "[DONE]") {
          break;
        }

        try {
          const chunk: TextToExcalidrawStreamChunk = JSON.parse(data);

          if (chunk === null) {
            break;
          }

          switch (chunk.type) {
            case "init":
              // New pipeline: correlation ID for tracing
              correlationId = chunk.correlationId;
              console.log("[Workflow] Correlation ID:", correlationId);
              break;
            case "keepalive":
              break;
            case "summary":
              // From Go to Draw: backend sends the summary for display and Regenerate
              if (chunk.summary) {
                onSummaryReceived?.(chunk.summary);
              }
              break;
            case "phase":
              // New pipeline: phase update
              onPhaseUpdate?.(chunk.phase, chunk.status);
              break;
            case "plan":
              // New pipeline: planner output
              onPlanReceived?.(chunk.search_plan);
              break;
            case "route":
              // New pipeline: router decision
              onRouteReceived?.(chunk.executor_plan);
              break;
            case "thought":
              // Agent reasoning content
              console.log(
                "[TextToExcalidraw] 💭 Thought received:",
                chunk.content?.substring(0, 100),
              );
              break;
            case "partial": {
              // Partial elements batch for progressive rendering
              console.log("[TextToExcalidraw] 📦 Partial elements batch:", {
                count: chunk.elements?.length || 0,
                totalCount: chunk.total_count,
                elementIds: chunk.elements?.map((el: any) => el.id) || [],
              });

              if (chunk.elements && Array.isArray(chunk.elements)) {
                partialElements.push(...chunk.elements);

                // Pass full accumulated list so caller can convert-and-replace scene (bound text needs container in same convert)
                onPartialElements?.([...partialElements], chunk.total_count);
              }
              break;
            }
            case "content": {
              const delta = chunk.delta;
              if (delta) {
                fullResponse += delta;
                onChunk?.(delta);
              }
              break;
            }
            case "optimized": {
              // Final optimized elements from backend
              console.log("[TextToExcalidraw] 📦 Received optimized event:", {
                hasElements: !!chunk.elements,
                elementCount: chunk.elements?.length || 0,
                hasFiles: !!chunk.files,
                fileCount: chunk.files ? Object.keys(chunk.files).length : 0,
                hasCode: !!chunk.code,
                metadata: chunk.metadata,
              });

              // Validate elements structure
              if (chunk.elements && Array.isArray(chunk.elements)) {
                console.log("[TextToExcalidraw] ✅ Elements is an array");

                // Check first few elements for required fields
                const sampleSize = Math.min(3, chunk.elements.length);
                for (let i = 0; i < sampleSize; i++) {
                  const el = chunk.elements[i];
                  console.log(`[TextToExcalidraw] Element ${i}:`, {
                    id: el.id,
                    type: el.type,
                    hasXY: el.x !== undefined && el.y !== undefined,
                    hasSize: el.width !== undefined && el.height !== undefined,
                    hasColors: !!el.strokeColor && !!el.backgroundColor,
                  });
                }
              } else {
                console.error(
                  "[TextToExcalidraw] ❌ Elements is not an array:",
                  typeof chunk.elements,
                );
              }

              // Validate files structure
              if (chunk.files) {
                console.log(
                  "[TextToExcalidraw] 📁 Files received:",
                  Object.keys(chunk.files),
                );

                // Check file format
                for (const [fileId, fileData] of Object.entries(chunk.files)) {
                  console.log(`[TextToExcalidraw] File ${fileId}:`, {
                    hasId: !!fileData.id,
                    hasDataURL: !!fileData.dataURL,
                    dataURLPrefix: fileData.dataURL?.substring(0, 30),
                    hasMimeType: !!fileData.mimeType,
                    mimeType: fileData.mimeType,
                    hasCreated: !!fileData.created,
                    created: fileData.created,
                  });

                  // Validate dataURL format
                  if (
                    !fileData.dataURL ||
                    !fileData.dataURL.startsWith("data:")
                  ) {
                    console.error(
                      `[TextToExcalidraw] ❌ Invalid dataURL for file ${fileId}`,
                    );
                  }
                }
              }

              optimizedElements = chunk.elements;
              optimizedFiles = chunk.files;
              fullResponse = chunk.code;
              break;
            }
            case "mermaid_result": {
              // New pipeline: Mermaid result - store for conversion
              mermaidCode = chunk.code;
              fullResponse = chunk.code;
              console.log("[Workflow] Mermaid result:", chunk.diagram_type);
              break;
            }
            case "processed": {
              // Processed code but parsing may have failed
              fullResponse = chunk.code;
              if (chunk.parseError) {
                console.warn("JSON parse warning:", chunk.parseError);
              }
              break;
            }
            case "safety_refusal":
              // Backend refused prompt; no generation. Return with safetyRefusal, no error.
              optimizedElements = undefined;
              fullResponse = "";
              return {
                safetyRefusal: {
                  message: chunk.message,
                  code: chunk.code,
                },
                correlationId,
              };
            case "quota_exceeded":
              // Account quota exceeded (24h window). No generation, clear message.
              optimizedElements = undefined;
              fullResponse = "";
              return {
                quotaExceeded: {
                  message: chunk.message,
                  code: chunk.code,
                  used: chunk.used,
                  limit: chunk.limit,
                  windowHours: chunk.windowHours,
                },
                correlationId,
              };
            case "error":
              error = new Error(chunk.error.message);
              break;
            case "done":
              break;
          }
        } catch (e) {
          console.warn("Failed to parse SSE data:", data, e);
        }
      }
    } catch (streamError: any) {
      if (streamError.name === "AbortError") {
        error = new Error("Request aborted");
      } else {
        const streamErr = new Error(streamError.message || "Streaming error");
        if ((streamError as any)?.status) {
          (streamErr as Error & { httpStatus?: number }).httpStatus = (
            streamError as any
          ).status;
        }
        error = streamErr;
      }
    }

    if (error) {
      const canRetry =
        internalRetryAttempt < MAX_NETWORK_RETRY &&
        isRetryableNetworkError(error);
      if (canRetry) {
        await waitForOnlineIfNeeded(signal);
        return TextToExcalidrawStreamFetch({
          ...options,
          __retryAttempt: internalRetryAttempt + 1,
        } as TextToExcalidrawOptions);
      }
      return { error, correlationId };
    }

    // If we have Mermaid code, convert it to Excalidraw elements
    if (mermaidCode) {
      try {
        console.log("[Workflow] Converting Mermaid to Excalidraw...");
        const parseResult = await parseMermaidToExcalidraw(mermaidCode);
        return {
          elements: parseResult.elements as ExcalidrawElementSkeleton[],
          rawCode: mermaidCode,
          correlationId,
        };
      } catch (e) {
        console.error("Failed to convert Mermaid to Excalidraw:", e);
        return {
          rawCode: mermaidCode,
          error: new Error("Failed to convert Mermaid diagram"),
          correlationId,
        };
      }
    }

    // If we have optimized elements from the backend, use them
    if (optimizedElements) {
      return {
        elements: optimizedElements,
        files: optimizedFiles,
        rawCode: fullResponse,
        correlationId,
      };
    }

    // Otherwise try to parse the raw response
    if (fullResponse) {
      try {
        const parsed = extractJsonArray(fullResponse);
        if (parsed) {
          return {
            elements: parsed,
            rawCode: fullResponse,
            correlationId,
          };
        }
      } catch (e) {
        console.warn("Failed to parse response as JSON:", e);
      }

      return {
        rawCode: fullResponse,
        error: new Error("Could not parse response as valid JSON array"),
        correlationId,
      };
    }

    return {
      error: new Error("No response received"),
      correlationId,
    };
  } catch (err: any) {
    const canRetry =
      internalRetryAttempt < MAX_NETWORK_RETRY &&
      isRetryableNetworkError(err);
    if (canRetry) {
      await waitForOnlineIfNeeded(signal);
      return TextToExcalidrawStreamFetch({
        ...options,
        __retryAttempt: internalRetryAttempt + 1,
      } as TextToExcalidrawOptions);
    }
    if (err.name === "AbortError") {
      return { error: new Error("Request aborted") };
    }
    return {
      error: new Error(err.message || "Request failed"),
    };
  }
}

/**
 * Extract JSON array from a string that might contain markdown or extra text
 */
function extractJsonArray(code: string): ExcalidrawElementSkeleton[] | null {
  // Strip markdown code fences
  let processed = code.trim();
  processed = processed.replace(/^```(?:json|javascript|js)?\s*\n?/i, "");
  processed = processed.replace(/\n?```\s*$/, "");
  processed = processed.trim();

  // Try to extract array
  const arrayMatch = processed.match(/\[[\s\S]*\]/);
  if (!arrayMatch) {
    return null;
  }

  try {
    const parsed = JSON.parse(arrayMatch[0]);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (e) {
    // Try to repair the JSON
    const repaired = repairJsonClosure(arrayMatch[0]);
    try {
      const parsed = JSON.parse(repaired);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (e2) {
      // Give up
    }
  }

  return null;
}

/**
 * Simple JSON closure repair
 */
function repairJsonClosure(input: string): string {
  if (!input || typeof input !== "string") {
    return input;
  }

  // Find start of JSON
  let start = -1;
  for (let i = 0; i < input.length; i++) {
    if (input[i] === "{" || input[i] === "[") {
      start = i;
      break;
    }
  }

  if (start === -1) {
    return input;
  }

  let inString = false;
  let escape = false;
  const stack: string[] = [];
  let out = "";

  for (let i = start; i < input.length; i++) {
    const ch = input[i];
    out += ch;

    if (inString) {
      if (escape) {
        escape = false;
        continue;
      }
      if (ch === "\\") {
        escape = true;
        continue;
      }
      if (ch === '"') {
        inString = false;
        continue;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === "{") {
      stack.push("}");
      continue;
    }
    if (ch === "[") {
      stack.push("]");
      continue;
    }
    if (ch === "}" || ch === "]") {
      if (stack.length && stack[stack.length - 1] === ch) {
        stack.pop();
      }
      if (stack.length === 0) {
        break;
      }
    }
  }

  // If still inside a string, close it
  if (inString) {
    out += '"';
  }

  // Remove trailing comma
  let i = out.length - 1;
  while (i >= 0 && /\s/.test(out[i])) {
    i--;
  }
  if (i >= 0 && out[i] === ",") {
    out = out.slice(0, i) + out.slice(i + 1);
  }

  // Append missing closers
  while (stack.length) {
    out += stack.pop();
  }

  return out;
}

/**
 * Get available chart types from the backend
 */
export async function getChartTypes(
  backendUrl: string,
): Promise<{ id: string; name: string }[]> {
  try {
    const response = await fetch(`${backendUrl}/v1/ai/generation/chart-types`);
    if (!response.ok) {
      throw new Error("Failed to fetch chart types");
    }
    const data = await response.json();
    return data.chartTypes || [];
  } catch (e) {
    console.error("Failed to fetch chart types:", e);
    // Return default chart types
    return [
      { id: "auto", name: "自动" },
      { id: "flowchart", name: "流程图" },
      { id: "mindmap", name: "思维导图" },
      { id: "orgchart", name: "组织架构图" },
      { id: "sequence", name: "时序图" },
      { id: "class", name: "UML类图" },
      { id: "er", name: "ER图" },
      { id: "gantt", name: "甘特图" },
      { id: "timeline", name: "时间线" },
      { id: "tree", name: "树形图" },
      { id: "network", name: "网络拓扑图" },
      { id: "architecture", name: "架构图" },
      { id: "dataflow", name: "数据流图" },
      { id: "state", name: "状态图" },
      { id: "swimlane", name: "泳道图" },
      { id: "concept", name: "概念图" },
      { id: "fishbone", name: "鱼骨图" },
      { id: "swot", name: "SWOT分析图" },
      { id: "pyramid", name: "金字塔图" },
      { id: "funnel", name: "漏斗图" },
      { id: "venn", name: "韦恩图" },
      { id: "matrix", name: "矩阵图" },
      { id: "infographic", name: "信息图" },
    ];
  }
}
